import React, { useState, useEffect, useRef, useCallback } from "react";
import { useEffectOnce } from "react-use";

import { styled, css } from "@mui/material";

export const CustomScroll = ({
  children,
  ...props
}: React.ComponentPropsWithoutRef<"div">) => {
  const contentRef = useRef<HTMLDivElement>(null);

  const scrollTrackRef = useRef<HTMLDivElement>(null);
  const scrollThumbRef = useRef<HTMLDivElement>(null);

  const observer = useRef<ResizeObserver | null>(null);
  const [thumbHeight, setThumbHeight] = useState(20);
  const [scrollStartPosition, setScrollStartPosition] = useState<number>(0);
  const [initialScrollTop, setInitialScrollTop] = useState<number>(0);
  const [isDragging, setIsDragging] = useState(false);

  function handleResize(ref: HTMLDivElement, trackSize: number) {
    const { clientHeight, scrollHeight } = ref;
    setThumbHeight(Math.max((clientHeight / scrollHeight) * trackSize, 20));
  }

  const handleTrackClick = useCallback(
    (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
      if (e.target === scrollThumbRef.current) {
        e.preventDefault();
        e.stopPropagation();
      }

      const { current: trackCurrent } = scrollTrackRef;
      const { current: contentCurrent } = contentRef;
      if (trackCurrent && contentCurrent) {
        const { clientY } = e;
        const target = e.target as HTMLDivElement;
        const rect = target.getBoundingClientRect();
        const trackTop = rect.top;
        const thumbOffset = -(thumbHeight / 2);
        const clickRatio =
          (clientY - trackTop + thumbOffset) / trackCurrent.clientHeight;
        const scrollAmount = Math.floor(
          clickRatio * contentCurrent.scrollHeight,
        );
        contentCurrent.scrollTo({
          top: scrollAmount,
          behavior: "smooth",
        });
      }
    },
    [thumbHeight],
  );

  const handleThumbPosition = useCallback(() => {
    if (
      !contentRef.current ||
      !scrollTrackRef.current ||
      !scrollThumbRef.current
    ) {
      return;
    }
    const { scrollTop: contentTop, scrollHeight: contentHeight } =
      contentRef.current;
    const { clientHeight: trackHeight } = scrollTrackRef.current;

    let newTop = (+contentTop / +contentHeight) * trackHeight;
    newTop = Math.min(newTop, trackHeight - thumbHeight);

    const thumb = scrollThumbRef.current;
    thumb.style.top = `${newTop}px`;
  }, [thumbHeight]);

  const handleThumbMousedown = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      if (e.target === scrollThumbRef.current) {
        e.preventDefault();
        e.stopPropagation();
      }
      setScrollStartPosition(e.clientY);
      if (contentRef.current) setInitialScrollTop(contentRef.current.scrollTop);
      setIsDragging(true);
    },
    [],
  );

  const handleThumbMouseup = useCallback(
    (e: MouseEvent) => {
      if (e.target === scrollThumbRef.current) {
        e.preventDefault();
        e.stopPropagation();
      }

      if (isDragging) {
        setIsDragging(false);
      }
    },
    [isDragging],
  );

  const handleThumbMousemove = useCallback(
    (e: MouseEvent) => {
      if (e.target === scrollThumbRef.current) {
        e.preventDefault();
        e.stopPropagation();
      }

      if (isDragging) {
        if (!contentRef.current) return;

        const {
          scrollHeight: contentScrollHeight,
          offsetHeight: contentOffsetHeight,
        } = contentRef.current;

        const deltaY =
          (e.clientY - scrollStartPosition) *
          (contentOffsetHeight / thumbHeight);
        const newScrollTop = Math.min(
          initialScrollTop + deltaY,
          contentScrollHeight - contentOffsetHeight,
        );

        contentRef.current.scrollTop = newScrollTop;
      }
    },
    [initialScrollTop, isDragging, scrollStartPosition, thumbHeight],
  );

  useEffectOnce(() => {
    if (contentRef.current && scrollTrackRef.current) {
      const ref = contentRef.current;
      const { clientHeight: trackSize } = scrollTrackRef.current;
      observer.current = new ResizeObserver(() => {
        handleResize(ref, trackSize);
      });
      observer.current.observe(ref);
      ref.addEventListener("scroll", handleThumbPosition);
      return () => {
        observer.current?.unobserve(ref);
        ref.removeEventListener("scroll", handleThumbPosition);
      };
    }

    return () => null;
  });

  useEffect(() => {
    document.addEventListener("mousemove", handleThumbMousemove);
    document.addEventListener("mouseup", handleThumbMouseup);
    document.addEventListener("mouseleave", handleThumbMouseup);
    return () => {
      document.removeEventListener("mousemove", handleThumbMousemove);
      document.removeEventListener("mouseup", handleThumbMouseup);
      document.removeEventListener("mouseleave", handleThumbMouseup);
    };
  }, [handleThumbMousemove, handleThumbMouseup]);

  const [scrollIsVisible, setScrollIsVisible] = useState(false);
  const mouseEnterHaldner = () => {
    setScrollIsVisible(true);
  };
  const mouseLeaveHaldner = () => {
    setScrollIsVisible(false);
  };

  return (
    <StyledContainer
      className="hidden lg:grid"
      onMouseEnter={mouseEnterHaldner}
      onMouseLeave={mouseLeaveHaldner}
    >
      <StyledContentWrapper ref={contentRef} {...props}>
        {children}
      </StyledContentWrapper>
      <StyledScrollbarWrapper>
        <StyledTrackAndThumbWrapper>
          <StyledTrack ref={scrollTrackRef} onClick={handleTrackClick} />
          <StyledThumb
            scrollIsVisible={scrollIsVisible}
            ref={scrollThumbRef}
            onMouseDown={handleThumbMousedown}
            style={{
              height: `${thumbHeight}px`,
            }}
          />
        </StyledTrackAndThumbWrapper>
      </StyledScrollbarWrapper>
    </StyledContainer>
  );
};

const StyledContainer = styled("div")`
  display: grid;
  grid-template: auto / auto 0;
  height: 100%;
  overflow: hidden;

  position: sticky;
  top: 0;
  flex-shrink: 0;
`;

const StyledContentWrapper = styled("div")`
  height: 100%;
  max-height: 100vh;

  -ms-overflow-style: none;
  overflow: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const StyledScrollbarWrapper = styled("div")`
  margin-left: -12px;
`;

const StyledTrackAndThumbWrapper = styled("div")`
  display: block;
  height: 100%;
  position: relative;
  width: 16px;
`;

const StyledTrack = styled("div")`
  border-radius: 12px;
  bottom: 0;
  position: absolute;
  top: 0;
  width: 4px;
`;

type PropTypes = {
  scrollIsVisible: boolean;
};

const StyledThumb = styled("div")<PropTypes>(({ scrollIsVisible }) => {
  return css`
    border-radius: 12px;

    transition: background-color 0.3s ease-in-out;
    background-color: rgba(181, 181, 181, 0.3);
    ${!scrollIsVisible && "background-color: rgba(181, 181, 181, 0);"}

    position: absolute;
    width: 4px;
  `;
});
